import React, { useEffect, useState } from 'react';
import { bool } from 'prop-types';

import classnames from 'classnames';
import CarouselFree, { CarouselFreeSlide } from '@andes/carousel-free';
import CarouselSnapped, { CarouselSnappedSlide } from '@andes/carousel-snapped';
import Button from '@andes/button';

import Header from '../components/header';
import CardItem from '../components/card-item';
import SeeMoreCard from '../components/see-more-card';
import { trackEvent } from '../../../lib/melidata';
import { NAME_SPACE } from './constants';
import { useStaticProps } from '../../context/static-props';
import useIntervention from './use-intervention';
import { interventionPropTypes } from '../../../components-v2/intervention/proptypes';
import { withComponentHandler } from '../../../hocs/with-component-handler';

const SEE_MORE_TYPE = 'SEE_MORE';

/**
 * @param {Object} props
 * @param {import("../../../components-v2/intervention/proptypes").InterventionJsDocs} props.intervention
 * @returns {React.JSX.Element?} The rendered component or null if no intervention is provided.
 */

const ShoppingCartIntervention = ({ intervention, mobile: isMobile }) => {
  const { deviceType } = useStaticProps() || {};
  const { header, dynamic_access, footer, store_id } = intervention;
  const mobile = deviceType === 'mobile' || isMobile;
  const isPadIntervention = store_id === 'PADS_INTERVENTION';
  const { currentInterventionData } = useIntervention();

  const handleClick = () => {
    const { track } = footer?.show_more || {};

    trackEvent({
      clean: true,
      path: track?.path,
      event_data: { ...track?.event_data },
    });
  };

  const superIntervention = Object.prototype.hasOwnProperty.call(header, 'pills');
  const superCarouselWithAddToCards = dynamic_access.cards.find((card) => card.content.item);
  const hasExtraSpacing = dynamic_access?.cards.some((card) => card.content.add_to_cart_item?.minimum_quantity > 0);
  const [dimensions, setDimensions] = useState({ height: 0, width: 0 });
  const SLIDES_PER_VIEW_TABLET = 2;
  const SLIDES_PER_VIEW_DESKTOP = 3;
  const REPURCHASE_SLIDES_PER_VIEW = 5;

  const hasOnlyTitle = Object.keys(header).length === 1 && Object.keys(header)[0] === 'title';
  const getSlidesPerView = () => {
    if (hasOnlyTitle) {
      return REPURCHASE_SLIDES_PER_VIEW;
    }

    return dimensions.width === 1024 ? SLIDES_PER_VIEW_TABLET : SLIDES_PER_VIEW_DESKTOP;
  };

  const interventionClassname = classnames(NAME_SPACE, {
    'repurchase-intervention-mobile': hasOnlyTitle && mobile,
    'repurchase-intervention-desktop': hasOnlyTitle && !mobile,
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setDimensions({
        height: window.innerHeight,
        width: window.innerWidth,
      });
    }
  }, []);

  return (
    <div className={interventionClassname}>
      {!hasOnlyTitle && (
        <Header
          {...header}
          devices={mobile}
          show_more={footer?.show_more}
          group_by={intervention?.store_id}
          currentInterventionData={currentInterventionData}
          nameSpace={NAME_SPACE}
        />
      )}
      {mobile ? (
        <>
          {hasOnlyTitle && <h2 className="andes-carousel-free-title">{header?.title?.text}</h2>}
          <CarouselFree spacing={12}>
            {dynamic_access?.cards.map((card, index) => {
              let track = null;

              if (card.type === SEE_MORE_TYPE) {
                track = footer?.show_more.track;
              }

              const content = {
                ...card.content,
                itemId: intervention.id,
                cardIndex: index,
                hasExtraSpacing,
              };

              if (track) {
                content.track = track;
              }

              return (
                <CarouselFreeSlide key={card.id}>
                  {card.type === SEE_MORE_TYPE ? <SeeMoreCard {...content} /> : <CardItem {...content} />}
                </CarouselFreeSlide>
              );
            })}
          </CarouselFree>
        </>
      ) : (
        <CarouselSnapped
          arrows={{ size: 'small' }}
          srLabel=""
          title={hasOnlyTitle && header?.title?.text}
          spacing={12}
          pagination={false}
          slidesPerView={getSlidesPerView()}
          strictBoundaries={false}
          lazyload
          className={superCarouselWithAddToCards && 'andes-carousel-snapped--super'}
        >
          {dynamic_access?.cards.map((card, index) => {
            let track = null;

            if (card.type === SEE_MORE_TYPE) {
              track = footer?.show_more.track;
            }

            const content = {
              ...card.content,
              itemId: intervention.id,
              cardIndex: index,
              hasExtraSpacing,
            };

            if (track) {
              content.track = track;
            }

            return (
              <CarouselSnappedSlide key={card.id} className="card-item--dynamic-width">
                {card.type === SEE_MORE_TYPE ? (
                  <SeeMoreCard {...content} />
                ) : (
                  <CardItem {...content} superIntervention={superIntervention} isPadIntervention={isPadIntervention} />
                )}
              </CarouselSnappedSlide>
            );
          })}
        </CarouselSnapped>
      )}
      {(mobile || deviceType === 'tablet') && footer?.show_more?.label?.text && (
        <div className={classnames('content-options', { 'content-options--tablet': deviceType === 'tablet' })}>
          <Button hierarchy="transparent" size="small" aria-hidden href={footer.show_more.target} onClick={handleClick}>
            {footer.show_more.label.text}
          </Button>
        </div>
      )}
    </div>
  );
};

ShoppingCartIntervention.propTypes = {
  ...interventionPropTypes,
  mobile: bool,
};

export default withComponentHandler(ShoppingCartIntervention, {
  componentName: 'ShoppingCartIntervention',
});
